import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { forkJoin } from "rxjs";
import { DetailsProps } from "./details";

export type Detail = {
  [key: string]: string;
};

export type Factor = {
  id: number;
  name: string;
  type: "number" | "percent" | "cost" | "hcc";
  currentImpactPercent: string;
  currentImpactValue: number;
  currentImpactDescription: string;
  prevImpactValue: number;
  currentStatus: "increase" | "decrease" | "same";
  flag: "R" | "G";
  enableDetails: boolean;
  details: Detail[];
};

export type Risk = {
  name: string;
  info: string;
  currentImpactPercent: string;
  flag: "R" | "G";
  prevImpactPercent: string;
  differencePercent: string;
  currentStatus: "increase" | "decrease" | "same";
  factors: Factor[];
};

export type UseRisksProps = {
  types: string[];
};

export type UseRisks = (props: UseRisksProps) => {
  patientID?: number,
  loading?: boolean;
  risks: Risk[];
  expanded: boolean;
  setexpanded?: (expanded: boolean) => void;
  detailsProps?: DetailsProps;
  showDetails: boolean;
  openDetails?: (type: string, factor: number) => void;
  closeDetails?: () => void;
};

const defaultRisksValue: Risk[] = [];

export const useRisks: UseRisks = function (props) {
  const patientID = useMemo(() => {
    const cMember = membersService?.getCurrentMember;

    return cMember?.patient_id;
  }, [membersService?.getCurrentMember]);

  const [loading, setloading] = useState<boolean>(false)
  const [risks, setrisks] = useState<Risk[]>(defaultRisksValue);

  useEffect(() => {
    setloading(true)
    if (props.types.length > 0) {
      let requests = props.types.map((type) => {
        return memberDetailsService.fetchRiskScore(patientID, type);
      });

      forkJoin(requests).subscribe((responses) => {
        const risks = responses
          .map((response) => {
            return response?.data?.data;
          })
          .filter((risk) => !!risk);

        setrisks(risks as Risk[]);
        setloading(false)
      });
    } else {
      setrisks([])
      setloading(false)
    }
  }, [props.types, props.types.join(","), props.types.length]);

  const [expanded, setexpanded] = useState<boolean>(true);

  const [detailsProps, setdetailsProps] = useState<DetailsProps>();
  const [showDetails, setshowDetails] = useState<boolean>(false);

  const openDetails = (type: string, factor: number) => {
    setdetailsProps({
      patientID,
      type,
      factor,
    });
    setshowDetails(true);
  };

  const closeDetails = () => {
    setshowDetails(false);
    setdetailsProps(undefined);
  };

  return {
    patientID,
    loading,
    risks,
    expanded,
    setexpanded,
    detailsProps,
    showDetails,
    openDetails,
    closeDetails,
  };
};

export const RisksContext = createContext<ReturnType<UseRisks>>({
  risks: defaultRisksValue,
  expanded: true,
  detailsProps: undefined,
  showDetails: false,
});

export const RisksProvider: React.FC<UseRisksProps> = function ({ children, ...props }) {
  const risksValue = useRisks(props);

  return <RisksContext.Provider value={risksValue}>{children}</RisksContext.Provider>;
};

export const UseRisksContext = () => useContext(RisksContext);
